<i18n>
{
  "de": {
    "videoNotPlayable": "Dieses Video kann im Moment nicht auf Deinem Gerät abgespielt werden.",
    "videoConverting": "Das Video wird gerade noch vorbereitet und steht dir bald zur Verfügung."
  },
  "en": {
    "videoNotPlayable": "This video is currently not playable on your device.",
    "videoConverting": "This video is currently converting."
  }
}
</i18n>

<template>
  <div>
    <div
      v-if="mediaAvailable"
      @contextmenu="handleContextMenu"
    >
      <div v-show="sourcesAvailable">
        <video
          :id="videoId"
          :ref="videoId"
          class="video-player-box video-js video-getnext"
        >
          <p class="vjs-no-js">
            To view this video please enable JavaScript, and consider upgrading to a
            web browser that
            <a
              href="http://videojs.com/html5-video-support/"
              target="_blank"
            >supports HTML5 video</a>
          </p>
        </video>
      </div>
      <div
        v-if="!sourcesAvailable"
        class="video-placeholder"
      >
        {{ $t('videoNotPlayable') }}
      </div>
    </div>
    <div
      v-else
      class="video-placeholder"
    >
      {{ $t('videoConverting') }}
    </div>
  </div>
</template>
<script>

import videojs from 'video.js'
require('videojs-contrib-quality-levels')
require('@/lib/player/hlsQualitySelector')
require('videojs-seek-buttons')

export default {
  props: ['video'],

  data() {
    return {
      hlsSource: false,
      player: null,
      seekButtons: {
        forward: 15,
        back: 15
      },
      options: {},
      vjsBoxEl: null,
      touchTracking: {
        startX: 0,
        startY: 0,
        startTime: 0,
        moved: false,
        threshold: 10, // pixels - movement beyond this is considered scrolling
        timeThreshold: 300 // ms - taps longer than this are ignored
      }
    }
  },
  computed: {
    videoId() {
      return `video_${this.video.name.replaceAll('-', '')}`
    },
    sourcesAvailable() { return (this.options.sources || []).length },
    mediaAvailable() { return this.video.master || this.video.path },
    aspectRatio() {
      return (this.video.variants && this.video.variants[0].displayAspectRatio) || '16:9'
    }
  },
  methods: {
    playPreview() {
      if (this.vjsBoxEl) {
        this.vjsBoxEl.classList.add('vjs-preview')
      }
      if (this.player) {
        this.player.currentTime(0)
        this.player.muted(true)
        this.player.play()
      }
    },
    stopPreview() {
      if (this.vjsBoxEl) {
        this.vjsBoxEl.classList.remove('vjs-preview')
      }
      if (this.player) {
        this.player.pause()
        this.player.hasStarted(false)
        this.player.trigger('ready')
      }
    },
    removePreviewListeners() {
      this.vjsBoxEl.classList.remove('vjs-preview')
      if (this.player) {
        this.player.currentTime(0)
        this.player.muted(false)
        if (this.vjsBoxEl) {
          this.vjsBoxEl.removeEventListener('mouseenter', this.playPreview)
          this.vjsBoxEl.removeEventListener('touchend', this.removePreviewListeners)
        }
        if (this.$refs[this.videoId]) {
          this.$refs[this.videoId].removeEventListener('mouseleave', this.stopPreview)
          this.$refs[this.videoId].removeEventListener('click', this.removePreviewListeners)
        }
        setTimeout(() => { this.player.play() }, 200)
      }
    },
    setSource() {
      if (this.mediaAvailable) {
        this.hlsSource = this.video.mimeType === 'application/x-mpegurl'
        if (this.hlsSource) {
          return [{
            type: this.video.mimeType,
            src: this.video.master
          }]
        }
        // Legacy
        return [{
          type: this.video.mimeType,
          src: this.video.path
        }]
      }
      return []
    },
    handleContextMenu(event) {
      event.preventDefault()
    },

    handleTouchStart(e) {
      if (e.target.nodeName === 'VIDEO') {
        const touch = e.touches[0]
        this.touchTracking.startX = touch.clientX
        this.touchTracking.startY = touch.clientY
        this.touchTracking.startTime = Date.now()
        this.touchTracking.moved = false
      }
    },

    handleTouchMove(e) {
      if (e.target.nodeName === 'VIDEO' && e.touches[0]) {
        const touch = e.touches[0]
        const deltaX = Math.abs(touch.clientX - this.touchTracking.startX)
        const deltaY = Math.abs(touch.clientY - this.touchTracking.startY)

        // If movement exceeds threshold, mark as moved (scrolling)
        if (deltaX > this.touchTracking.threshold || deltaY > this.touchTracking.threshold) {
          this.touchTracking.moved = true
        }
      }
    },

    handleTouchEnd(e) {
      if (e.target.nodeName === 'VIDEO') {
        const touchDuration = Date.now() - this.touchTracking.startTime

        // Only trigger play/pause if:
        // 1. Touch didn't move significantly (not scrolling)
        // 2. Touch duration was short (quick tap)
        if (!this.touchTracking.moved && touchDuration < this.touchTracking.timeThreshold) {
          if (this.player.paused()) {
            this.player.play()
          } else {
            this.player.pause()
          }
        }

        // Reset tracking
        this.touchTracking.moved = false
      }
    },

    init() {
      if (!this.$refs[this.videoId]) {
        console.log('Video player not initialized for id', this.videoId)
        return
      }

      this.options = {
        fluid: true,
        aspectRatio: this.aspectRatio,
        playsinline: true,
        autoplay: false,
        controls: true,
        preload: 'auto',
        controlBar: {
          volumePanel: { inline: true }
        },
        poster: this.video.poster || '',
        sources: this.setSource(),
        language: this.$i18n.locale,
        userActions: {
          hotkeys: event => {
            switch (event.code) {
              case 'Space':
                if (this.player.paused()) {
                  this.player.play()
                } else {
                  this.player.pause()
                }
                break
              case 'ArrowRight':
                this.player.currentTime(this.player.currentTime() + this.seekButtons.forward)
                break
              case 'ArrowLeft':
                this.player.currentTime(this.player.currentTime() - this.seekButtons.back)
                break
              case 'ArrowUp':
                this.player.volume(this.player.volume() + 0.05)
                break
              case 'ArrowDown':
                this.player.volume(this.player.volume() - 0.05)
                break
              case 'KeyM':
                if (this.player.muted()) {
                  this.player.muted(false)
                } else {
                  this.player.muted(true)
                }
                break
              case 'KeyF':
                if (this.player.isFullscreen()) {
                  this.player.exitFullscreen()
                } else {
                  this.player.requestFullscreen()
                }
                break

              default:
                break
            }
          }
        },
        html5: {
          vhs: {
            overrideNative: true
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false
        }
      }
      this.player = videojs(this.$refs[this.videoId], this.options)
      this.player.hlsQualitySelector({
        displayCurrentQuality: true
      })
      this.player.seekButtons({
        forward: 10,
        back: 10
      })

      this.player.on('ready', () => {

      })
      this.player.on('ended', () => { })
      this.player.on('play', () => { })
      this.player.on('pause', () => { })
      this.player.on('error', () => { })
      this.player.on('waiting', () => { })
      this.player.on('emptied', () => { })

      // Add smart touch detection for tap-to-play/pause
      this.player.on('touchstart', this.handleTouchStart)
      this.player.on('touchmove', this.handleTouchMove)
      this.player.on('touchend', this.handleTouchEnd)

      this.vjsBoxEl = this.$refs[this.videoId].closest('.video-player-box')
      if (this.vjsBoxEl) {
        this.vjsBoxEl.addEventListener('mouseenter', this.playPreview)
        this.vjsBoxEl.addEventListener('touchend', this.removePreviewListeners)
      }

      this.$refs[this.videoId].addEventListener('click', this.removePreviewListeners)
      this.$refs[this.videoId].addEventListener('mouseleave', this.stopPreview)
    }
  },
  mounted() {
    setTimeout(() => {
      this.init()
    }, 200)
  },
  beforeDestroy() {
    if (this.player) {
      this.player.off('ready')
      this.player.off('ended')
      this.player.off('play')
      this.player.off('pause')
      this.player.off('error')
      this.player.off('waiting')
      this.player.off('emptied')

      // Clean up smart touch detection listeners
      this.player.off('touchstart', this.handleTouchStart)
      this.player.off('touchmove', this.handleTouchMove)
      this.player.off('touchend', this.handleTouchEnd)

      if (this.vjsBoxEl) {
        this.vjsBoxEl.removeEventListener('mouseenter', this.playPreview)
      }
      if (this.$refs[this.videoId]) {
        this.$refs[this.videoId].removeEventListener('mouseleave', this.stopPreview)
        this.$refs[this.videoId].removeEventListener('click', this.removePreviewListeners)
      }

      this.player.dispose()
    }
  }
}
</script>

<style lang="scss">
@import 'assets/sass/_variables';
@import 'assets/sass/video-player';

.video-placeholder {
  width: 100%;
  font-style: italic;
  background-color: #b0b0b0;
}

.vjs-poster {
  background-size: cover;
}

.vjs-preview {
  .vjs-control-bar {
    background-color: transparent !important;

    .vjs-progress-control {
      top: 25px !important;
    }

    &>*:not(.vjs-progress-control) {
      display: none !important;
    }
  }
}

@media screen and (max-width: $mobile) {
  .vjs-seek-button {
    display: none !important;
  }
}
</style>
